import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  Stack,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import {
  Folder as FolderIcon,
  TableChart as TableIcon,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function ObjectExplorer({ tables = [], filter = "" }) {
  const navigate = useNavigate();
  const q = (filter || "").trim().toLowerCase();
  const [openSchemas, setOpenSchemas] = useState({});

  const filteredTables = useMemo(() => {
    if (!q) return tables;
    return tables.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(q) ||
        (t.schema || "").toLowerCase().includes(q) ||
        (t.fullName || "").toLowerCase().includes(q)
    );
  }, [tables, q]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filteredTables) {
      const schema = t.schema || "dbo";
      if (!map.has(schema)) map.set(schema, []);
      map.get(schema).push(t);
    }
    return [...map.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([schema, list]) => [
        schema,
        list.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
      ]);
  }, [filteredTables]);

  const toggleSchema = (schema) => {
    setOpenSchemas((prev) => ({ ...prev, [schema]: !prev[schema] }));
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Chip size="small" label={`Tables: ${filteredTables.length}`} variant="outlined" />
      </Stack>

      <List dense disablePadding>
        {grouped.map(([schema, list]) => {
          const isOpen = openSchemas[schema] ?? true; // default open
          return (
            <Box key={schema}>
              <ListItemButton onClick={() => toggleSchema(schema)} sx={{ borderRadius: 1 }}>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <FolderIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">{schema}</Typography>
                      <Chip size="small" label={list.length} variant="outlined" />
                    </Stack>
                  }
                />
                {isOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>

              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <List dense disablePadding sx={{ pl: 1 }}>
                  {list.map((t) => (
                    <ListItemButton
                      key={`${schema}.${t.name}`}
                      onClick={() => navigate(`/table/${schema}/${t.name}`)}
                      sx={{ borderRadius: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <TableIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography variant="body2" noWrap>
                            {t.name}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
}
